import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, UserX } from 'lucide-react';
import { EarningsKPI } from '../EarningsKPI';
import { EmptyState } from '../EmptyState';
import { useBookingActions } from '../../lib/useBookingActions';
import { fmtTime, chipFor, DAY_NAMES, MONTHS, greeting } from '../../lib/dashboard/helpers';

/**
 * BARBER DASHBOARD — trimmed. Only what's relevant to the individual
 * barber: their own earnings card + their own bookings today. No
 * shop-wide KPI row, no channel split, no week-bar chart. Data comes
 * purely through props from `DashboardInner` (which already fetched
 * everything with the RLS-scoped queries).
 */

const BarberRowActions = ({ booking, perms, onChanged }) => {
  const a = useBookingActions(booking, onChanged, perms);
  const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(); };
  return (
    <div className="appt-actions" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      {a.can.complete && (
        <button type="button" className="mini-btn ok" disabled={a.busy} onClick={stop(a.markCompleted)} title="Mark completed" data-testid={`barber-appt-complete-${booking.id}`}><Check size={14} /></button>
      )}
      {a.can.noShow && (
        <button type="button" className="mini-btn warn" disabled={a.busy} onClick={stop(a.markNoShow)} title="Mark no-show" data-testid={`barber-appt-noshow-${booking.id}`}><UserX size={14} /></button>
      )}
      {a.can.cancel && (
        <button type="button" className="mini-btn danger" disabled={a.busy} onClick={stop(a.cancelBooking)} title="Cancel" data-testid={`barber-appt-cancel-${booking.id}`}><X size={14} /></button>
      )}
      {a.confirmNode}
      {a.rescheduleNode}
      {a.paymentSheetNode}
    </div>
  );
};

export const BarberDashboard = ({ profile, shop, myBarberId, myPerms, todayList, loading, reload }) => {
  const now = new Date();
  const firstName = profile?.full_name?.split(' ')[0] || 'there';
  const dayLabel = DAY_NAMES[now.getDay()];
  const monthName = MONTHS[now.getMonth()];
  const todayCount = todayList?.length || 0;

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2 data-testid="barber-dashboard-greeting">{greeting(now.getHours())}, {firstName}</h2>
          <div className="sub">{shop?.name || 'Your chair'} · {dayLabel} {now.getDate()} {monthName}</div>
        </div>
        <div className="av">{(profile?.full_name || 'B').charAt(0).toUpperCase()}</div>
      </div>

      <div className="body" data-testid="barber-dashboard-body">
        <div className="kpis">
          <EarningsKPI shopId={profile?.shop_id} barberId={myBarberId} label="Your earnings · this week" dataTestId="kpi-barber-earnings" />
          <div className="kpi" data-testid="kpi-barber-today">
            <div className="t">Your bookings today</div>
            <div className="v">{loading ? '—' : todayCount}</div>
            <div className="d flat">{loading ? '—' : (todayCount === 0 ? 'nothing on today' : `${todayCount} on the books`)}</div>
          </div>
        </div>

        <div className="panel" data-testid="barber-today" style={{ marginTop: 14 }}>
          <div className="ph">
            <h3>Your bookings today ({todayCount})</h3>
            <Link to="/diary" className="link">Open diary →</Link>
          </div>
          {loading ? (
            <div style={{ padding: 16 }}>
              {[1, 2, 3].map((i) => <div key={i} className="skel-line" style={{ width: `${60 + i * 10}%` }} />)}
            </div>
          ) : todayCount === 0 ? (
            <EmptyState title="No bookings today" detail="Your chair is clear — enjoy the breathing room." dataTestId="barber-today-empty" compact />
          ) : (
            todayList.map((b) => {
              const closed = ['completed', 'no_show', 'cancelled'].includes(b.status);
              const showActions = myPerms?.canManageBookings === true && !closed;
              return (
                <Link
                  key={b.id}
                  to={`/diary?date=${b.starts_at.slice(0, 10)}&booking=${b.id}`}
                  className={`appt ${closed ? `closed s-${b.status}` : ''}`}
                  data-testid={`barber-appt-${b.id}`}
                  style={{ textDecoration: 'none' }}
                >
                  <span className="time">{fmtTime(b.starts_at)}</span>
                  <span className="bar" />
                  <div className="who">
                    <b>{b.customer?.name || '—'}</b>
                    <small>{b.service?.name} · <span className="mono">{b.service?.duration_min}</span> min</small>
                  </div>
                  <span className="amt">£{Number(b.service?.price || 0).toFixed(0)}</span>
                  {(() => {
                    const chip = chipFor(b);
                    return <span className={`pay-chip ${chip.cls}`} data-chip-state={chip.cls}>{chip.label}</span>;
                  })()}
                  {showActions && (
                    <BarberRowActions
                      booking={b}
                      perms={{
                        canManageBookings: myPerms?.canManageBookings === true,
                        canTakePayments: myPerms?.canTakePayments === true,
                      }}
                      onChanged={reload}
                    />
                  )}
                </Link>
              );
            })
          )}
        </div>
      </div>
    </main>
  );
};
